import type { ReactNode } from 'react';
import Sheet from '../ui/Sheet';
import { Paperclip, UserPlus, UserMinus, CheckSquare, LogOut, ChevronRight } from '../../lib/icons';

interface Props {
  isClientThread: boolean;
  isOrgThread: boolean;
  /** Current non-self participant names, for the "Remove a teammate" row's description. */
  participantNames: string[];
  onAttach: () => void;
  onAddTeammate: () => void;
  onRemoveTeammate: () => void;
  onCreateTask: () => void;
  onLeave: () => void;
  onClose: () => void;
}

interface Row {
  icon: ReactNode;
  iconBg: string;
  iconColor: string;
  title: string;
  titleColor?: string;
  description: string;
  onClick: () => void;
  chevron?: boolean;
}

function truncateNames(names: string[]): string {
  if (names.length === 0) return 'No one else is in this thread';
  if (names.length <= 3) return names.join(', ');
  return `${names.slice(0, 3).join(', ')} +${names.length - 3} more`;
}

// Screen 3c — surfaces the actions previously only discoverable by typing
// //add, //remove, //task, //leave. Additive discoverability: the parser in
// MessageInput.tsx is untouched, slash commands still work underneath.
export default function ComposerPlusMenu({
  isClientThread, isOrgThread, participantNames,
  onAttach, onAddTeammate, onRemoveTeammate, onCreateTask, onLeave, onClose,
}: Props) {
  const rows: Row[] = [
    {
      icon: <Paperclip size={17} strokeWidth={1.8} />,
      iconBg: 'rgba(29,158,117,.16)', iconColor: 'var(--v2)',
      title: 'Attach a file',
      description: 'Image, PDF, doc or txt · up to 25 MB',
      onClick: onAttach,
    },
  ];

  if (isClientThread) {
    rows.push({
      icon: <UserPlus size={17} strokeWidth={1.8} />,
      iconBg: 'rgba(55,138,221,.16)', iconColor: 'var(--blue-soft)',
      title: 'Add a teammate',
      description: "They see the chain and its notes, not the client",
      onClick: onAddTeammate,
      chevron: true,
    });
    rows.push({
      icon: <UserMinus size={17} strokeWidth={1.8} />,
      iconBg: 'var(--hover-strong)', iconColor: 'var(--muted)',
      title: 'Remove a teammate',
      description: truncateNames(participantNames),
      onClick: onRemoveTeammate,
      chevron: true,
    });
  }

  rows.push({
    icon: <CheckSquare size={17} strokeWidth={1.8} />,
    iconBg: 'rgba(159,122,234,.16)', iconColor: 'var(--purple)',
    title: 'Create a task',
    description: 'Title, assignees and a due date',
    onClick: onCreateTask,
  });

  if (!isOrgThread) {
    rows.push({
      icon: <LogOut size={17} strokeWidth={1.8} />,
      iconBg: 'rgba(226,75,74,.14)', iconColor: 'var(--red)',
      title: 'Leave this thread',
      titleColor: 'var(--red)',
      description: 'Your past messages and notes stay in the chain',
      onClick: onLeave,
    });
  }

  return (
    <Sheet title="Add to this thread" onClose={onClose}>
      <div style={{ padding: '4px 10px 8px' }}>
        {rows.map((row) => (
          <button
            key={row.title}
            onClick={() => { row.onClick(); onClose(); }}
            style={{
              display: 'flex', alignItems: 'center', gap: 12, width: '100%',
              padding: '10px 8px', background: 'none', border: 'none', borderRadius: 6,
              textAlign: 'left', cursor: 'pointer',
            }}
          >
            <div style={{
              width: 38, height: 38, borderRadius: 9, flexShrink: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: row.iconBg, color: row.iconColor,
            }}>
              {row.icon}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: '0.85rem', fontWeight: 600, color: row.titleColor ?? 'var(--text-primary)' }}>
                {row.title}
              </div>
              <div style={{
                fontSize: '0.72rem', color: 'var(--text-tertiary)',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>
                {row.description}
              </div>
            </div>
            {row.chevron && <ChevronRight size={16} strokeWidth={1.8} style={{ color: 'var(--shell-xs)', flexShrink: 0 }} />}
          </button>
        ))}
      </div>
      <div style={{ padding: '10px 18px 16px', fontFamily: 'var(--font-mono)', fontSize: '0.68rem', color: 'var(--shell-xs)' }}>
        Prefer typing? //add //remove //task //leave still work.
      </div>
    </Sheet>
  );
}
